import { APIError, ClientError, useErrorHandler, useQueryClient } from 'shared-ui';
import { toast } from 'react-toastify';

import { deletePrzejscie } from 'api/strefy/strefy';
import { StrefyErrorCode } from 'api/strefy/strefy.enum';
import { StrefyErrorResponse } from 'api/strefy/strefy.types';
import { QueryKeyEnum } from 'core/global.enum';
import { useLocale } from 'hooks/useLocale/useLocale';
import { useModalState } from 'hooks/useModalState/useModalState';
import { useMutation } from 'hooks/useMutation/useMutation';
import { AppMessages } from 'i18n/messages';

import { DeletePassage } from './DeletePassage';
import { DeletePassageContainerProps } from './DeletePassage.types';

export const DeletePassageContainer = ({ passage, isDisabled, disabledReason }: DeletePassageContainerProps) => {
  const queryClient = useQueryClient();
  const { formatMessage } = useLocale();
  const { handleError } = useErrorHandler();
  const { isModalOpen, onModalOpen, onModalClose } = useModalState();

  // The list, the server counters, the readers offered by "Dodaj przejście", the zones' `liczbaPrzejsc`
  // and the monitoring's `liczbaPrzejsc` (Start button) all change with a deleted przejście.
  const invalidatePassages = () => {
    queryClient.invalidateQueries([QueryKeyEnum.PRZEJSCIA]);
    queryClient.invalidateQueries([QueryKeyEnum.PRZEJSCIE_SERWERY]);
    queryClient.invalidateQueries([QueryKeyEnum.PRZEJSCIE_DOSTEPNE_TKD]);
    queryClient.invalidateQueries([QueryKeyEnum.STREFY]);
    queryClient.invalidateQueries([QueryKeyEnum.MONITORING_OBECNOSCI]);
  };

  const { mutate: onDeletePassage, isLoading } = useMutation(deletePrzejscie, {
    onSuccess: () => {
      invalidatePassages();
      toast.dark(formatMessage({ id: AppMessages['definitions.passages.delete.success'] }));
      onModalClose();
    },
    onError: (error: ClientError<APIError<StrefyErrorResponse>>) => {
      const code = error.response?.data.code;
      onModalClose();

      if (code === StrefyErrorCode.MONITORING_AKTYWNY) {
        queryClient.invalidateQueries([QueryKeyEnum.MONITORING_OBECNOSCI]);
        toast.dark(formatMessage({ id: AppMessages['definitions.passages.monitoringActive'] }));
        return;
      }

      if (code === StrefyErrorCode.PRZEJSCIE_NIE_ISTNIEJE) {
        invalidatePassages();
      }

      handleError({ error });
    },
  });

  return (
    <DeletePassage
      passage={passage}
      isDisabled={isDisabled}
      disabledReason={disabledReason}
      isModalOpen={isModalOpen}
      onModalOpen={onModalOpen}
      onModalClose={onModalClose}
      isLoading={isLoading}
      onSubmit={() => onDeletePassage(passage.id)}
    />
  );
};
